import I18N_STRINGS from 'Jsx/CheckoutV4/i18n/language';
import { CreditCardIssuer } from 'Jsx/CheckoutV4/types';

import {
  getCreditCardIssuer,
  validateCardNumber,
} from './validateCreditCardElements';

const getCreditCardNumber = (
  inputEvent: React.ChangeEvent<HTMLInputElement>,
) => {
  const { defaultValue, value } = inputEvent.target;
  let creditCardNumberErrorMessage = '';
  let creditCardNumberMask = '';
  let isCreditCardNumberValid = false;
  let cardDigits = 16;
  const numberEntered = value.trim().replace(/[^0-9\s]/g, '');
  const creditCardIssuer = getCreditCardIssuer(numberEntered);
  const amexCardMask = /^\d{4}\s\d{6}\s\d{5}$/;
  const visaOrOtherCardMask = /^\d{4}\s\d{4}\s\d{4}\s\d{2,4}$/;
  const creditCardDigitsWithBlankSpaces =
    creditCardIssuer === CreditCardIssuer.AMEX ||
    creditCardIssuer === CreditCardIssuer.DINERS
      ? 17
      : 19;

  if (defaultValue.length < numberEntered.length) {
    if (creditCardIssuer === CreditCardIssuer.AMEX) {
      cardDigits = 15; // MASK: 3714 496353 98431
      if (numberEntered.replace(/\s/g, '').length === cardDigits) {
        creditCardNumberMask = numberEntered
          .replace(/\s/g, '')
          .replace(/^(\d{4})(\d{6})(\d{5})$/g, '$1 $2 $3');
      } else {
        creditCardNumberMask =
          numberEntered.length === 4 || numberEntered.length === 11
            ? `${numberEntered} `
            : numberEntered;
      }
    } else {
      if (creditCardIssuer === CreditCardIssuer.DINERS) {
        // MASK: 3622 7206 2716 67
        cardDigits = 14;
      }
      // other credit cards
      if (numberEntered.replace(/\s/g, '').length === cardDigits) {
        creditCardNumberMask = numberEntered
          .replace(/\s/g, '')
          .replace(/^(\d{4})(\d{4})(\d{4})(\d{2,4})$/g, '$1 $2 $3 $4');
      } else {
        creditCardNumberMask =
          numberEntered.replace(/\s+/g, '').length % 4 === 0 &&
          numberEntered.length < creditCardDigitsWithBlankSpaces
            ? `${numberEntered} `
            : numberEntered;
      }
    }
  } else {
    creditCardNumberMask = numberEntered;
  }

  if (creditCardNumberMask.length === creditCardDigitsWithBlankSpaces) {
    const isCreditCardNumberFormatValid =
      creditCardIssuer === CreditCardIssuer.AMEX
        ? amexCardMask.test(creditCardNumberMask)
        : visaOrOtherCardMask.test(creditCardNumberMask);

    isCreditCardNumberValid =
      isCreditCardNumberFormatValid && validateCardNumber(creditCardNumberMask);

    creditCardNumberErrorMessage = !isCreditCardNumberValid
      ? I18N_STRINGS.INVALID_CARD_NUMBER
      : '';
  } else {
    isCreditCardNumberValid = false;
    creditCardNumberErrorMessage =
      creditCardNumberMask.length === 0
        ? I18N_STRINGS.REQUIRED_CARD_NUMBER
        : I18N_STRINGS.INCOMPLETE_CARD_NUMBER;
  }

  return {
    isCreditCardNumberValid,
    creditCardIssuer,
    creditCardNumberMask,
    creditCardDigitsWithBlankSpaces,
    creditCardNumberErrorMessage,
  };
};

export default getCreditCardNumber;

export const validateCreditCardNumber = (
  inputEvent: React.ChangeEvent<HTMLInputElement>,
): {
  creditCardNumberErrorMessage: string;
  creditCardNumberMask?: string;
  creditCardDigitsWithBlankSpaces?: number;
  isCreditCardNumberValid: boolean;
} => {
  const {
    creditCardNumberMask,
    creditCardDigitsWithBlankSpaces,
    isCreditCardNumberValid,
  } = getCreditCardNumber(inputEvent);
  let creditCardNumberErrorMessage = '';

  if (!creditCardNumberMask) {
    creditCardNumberErrorMessage = I18N_STRINGS.REQUIRED_CARD_NUMBER;
  } else if (creditCardNumberMask.length < creditCardDigitsWithBlankSpaces) {
    creditCardNumberErrorMessage = I18N_STRINGS.INCOMPLETE_CARD_NUMBER;
  } else if (!isCreditCardNumberValid) {
    creditCardNumberErrorMessage = I18N_STRINGS.INVALID_CARD_NUMBER;
  }

  return {
    creditCardNumberErrorMessage,
    creditCardNumberMask,
    creditCardDigitsWithBlankSpaces,
    isCreditCardNumberValid,
  };
};

export const getCreditCardBIN = ({
  creditCardNumber,
}: {
  creditCardNumber: string;
}): { creditCardBin: number } => {
  const newCreditCardNumber = creditCardNumber.replace(/\s+/g, '').slice(0, 6);

  const creditCardBin = parseInt(newCreditCardNumber, 10);

  return { creditCardBin };
};
